/**
 * Cart provider.
 *
 * The cart lives on the server; the browser only keeps its id. Every mutation
 * returns the freshly priced cart, so the totals on screen are always the ones
 * the server computed — the client never does its own arithmetic on money.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiClientError, type CartDto } from './api';

const CART_ID_KEY = 'gills.cartId.v1';

interface CartContextValue {
  cart: CartDto | null;
  loading: boolean;
  /** Set when the last action failed, for inline messaging. */
  error: string | null;
  clearError: () => void;
  itemCount: number;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  add: (productId: string, quantity?: number) => Promise<boolean>;
  setQuantity: (productId: string, quantity: number) => Promise<void>;
  remove: (productId: string) => Promise<void>;
  applyCoupon: (code: string) => Promise<void>;
  removeCoupon: () => Promise<void>;
  update: (patch: Record<string, unknown>) => Promise<void>;
  refresh: () => Promise<void>;
  /** Called after checkout so the badge empties immediately. */
  reset: () => Promise<void>;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Guards against two "add to cart" clicks each creating their own cart.
  const ensuring = useRef<Promise<CartDto> | null>(null);

  const ensureCart = useCallback(async (): Promise<CartDto> => {
    if (cart) return cart;
    if (ensuring.current) return ensuring.current;

    ensuring.current = (async () => {
      const existingId = localStorage.getItem(CART_ID_KEY);
      if (existingId) {
        try {
          const found = await api.getCart(existingId);
          setCart(found);
          return found;
        } catch (err) {
          // A 404 means the cart aged out of its 30-day TTL; start a new one
          // rather than leaving the shopper unable to add anything.
          if (!(err instanceof ApiClientError) || err.status !== 404) throw err;
          localStorage.removeItem(CART_ID_KEY);
        }
      }
      const created = await api.createCart();
      localStorage.setItem(CART_ID_KEY, created.id);
      setCart(created);
      return created;
    })();

    try {
      return await ensuring.current;
    } finally {
      ensuring.current = null;
    }
  }, [cart]);

  // Load an existing cart on first mount, but never create one — a visitor who
  // only browses should not generate a database write.
  useEffect(() => {
    const existingId = localStorage.getItem(CART_ID_KEY);
    if (!existingId) return;

    let cancelled = false;
    void api
      .getCart(existingId)
      .then((found) => {
        if (!cancelled) setCart(found);
      })
      .catch(() => {
        localStorage.removeItem(CART_ID_KEY);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const run = useCallback(
    async (action: (cartId: string) => Promise<CartDto>): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        const current = await ensureCart();
        setCart(await action(current.id));
        return true;
      } catch (err) {
        setError(
          err instanceof ApiClientError
            ? err.message
            : 'Something went wrong. Please try again.',
        );
        return false;
      } finally {
        setLoading(false);
      }
    },
    [ensureCart],
  );

  const add = useCallback(
    async (productId: string, quantity = 1) => {
      const ok = await run((id) => api.addToCart(id, productId, quantity));
      if (ok) setIsOpen(true);
      return ok;
    },
    [run],
  );

  const setQuantity = useCallback(
    async (productId: string, quantity: number) => {
      if (quantity <= 0) {
        await run((id) => api.removeFromCart(id, productId));
        return;
      }
      await run((id) => {
        const items = (cart?.items ?? []).map((item) =>
          item.productId === productId ? { ...item, quantity } : item,
        );
        return api.updateCart(id, { items });
      });
    },
    [run, cart],
  );

  const remove = useCallback(
    async (productId: string) => {
      await run((id) => api.removeFromCart(id, productId));
    },
    [run],
  );

  const applyCoupon = useCallback(
    async (code: string) => {
      await run((id) => api.updateCart(id, { couponCode: code }));
    },
    [run],
  );

  const removeCoupon = useCallback(async () => {
    await run((id) => api.updateCart(id, { couponCode: null }));
  }, [run]);

  const update = useCallback(
    async (patch: Record<string, unknown>) => {
      await run((id) => api.updateCart(id, patch));
    },
    [run],
  );

  const refresh = useCallback(async () => {
    if (!cart) return;
    try {
      setCart(await api.getCart(cart.id));
    } catch {
      // A refresh failure leaves the previous totals on screen, which is
      // better than blanking the basket.
    }
  }, [cart]);

  const reset = useCallback(async () => {
    localStorage.removeItem(CART_ID_KEY);
    setCart(null);
    setIsOpen(false);
  }, []);

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      loading,
      error,
      clearError: () => setError(null),
      itemCount: cart?.totals.totalQuantity ?? 0,
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      add,
      setQuantity,
      remove,
      applyCoupon,
      removeCoupon,
      update,
      refresh,
      reset,
    }),
    [cart, loading, error, isOpen, add, setQuantity, remove, applyCoupon, removeCoupon, update, refresh, reset],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used inside <CartProvider>');
  return context;
}
